import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { t, type IntlService } from 'ember-intl';
import { PhSparkle, PhArrowRight } from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
} from 'tio-employee/services/reimagined-data';

interface TasApplicationsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
}

interface TasApplicationsSignature {
  Args: {
    model: TasApplicationsModel;
  };
}

export default class CareerPathwaysTasApplicationsIndex extends Component<TasApplicationsSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  <template>
    <VStack class="gap-6">
      <div
        class="rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500 p-6 text-white shadow-lg"
      >
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl">
            <div
              class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
            >
              <PhSparkle class="h-4 w-4" />
              {{t "tuition_assistance.reimagined.applications.hero_eyebrow"}}
            </div>
            <Heading @level={{1}} class="text-white">
              {{t "tuition_assistance.reimagined.applications.hero_title"}}
            </Heading>
            <Text class="text-white/80">
              {{t "tuition_assistance.reimagined.applications.hero_subtitle"}}
            </Text>
          </div>
          <LinkTo @route="authenticated.tas.dashboard">
            <Button @intent="primary">{{t
                "tuition_assistance.reimagined.applications.cta_start"
              }}</Button>
          </LinkTo>
        </div>
      </div>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-applications"
      >
        <Section>
          <:header>
            <Heading @level={{2}}>{{t
                "tuition_assistance.reimagined.applications.section_recent"
              }}</Heading>
          </:header>
          <:body>
            {{#if this.applications.length}}
              <div class="grid grid-cols-1 gap-4">
                {{#each this.applications as |application|}}
                  <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{application.name}}</Heading>
                        <Text @intent="muted">{{application.programName}}</Text>
                        {{#if application.coursesBeginDate}}
                          <Text @intent="muted">
                            {{t
                              "tuition_assistance.reimagined.applications.label_starts"
                              date=application.coursesBeginDate
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <div class="flex items-center gap-2">
                        <Chip @intent="default">{{application.state}}</Chip>
                        <Chip @intent="primary">{{application.paymentState}}</Chip>
                      </div>
                    </div>
                    <div class="mt-4 flex items-center gap-4">
                      <Text @intent="muted">
                        {{t
                          "tuition_assistance.reimagined.applications.label_approved"
                          amount=(this.formatCurrency application.approvedTotal)
                        }}
                      </Text>
                      <LinkTo
                        @route="authenticated.tas.applications.show"
                        @model={{application.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tuition_assistance.reimagined.applications.link_review"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.applications.empty_title"
                  }}</Heading>
                <Text @intent="muted">
                  {{t "tuition_assistance.reimagined.applications.empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.applications.empty_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
